/**
 * Self-test suite for the Blocked-users settings view-model layer.
 *
 * Five groups — mirrors `kaether_for_ios/Stoat/E2EE/BlockedUsersSelfTest.swift`:
 *
 *   A. Row builder — naming rule (trusted-roster only), age buckets.
 *   B. Filter — substring match, case-insensitivity, whitespace trim,
 *      empty query passthrough.
 *   C. Sort — three orders, stable tiebreaker.
 *   D. Unblock handler — proceed / skip outcomes.
 *   E. Hardening — displayName for non-roster never includes peer-supplied
 *      bytes; filter is in-memory only (synchronous, no network surface).
 *
 * All store-touching cases (group D) use isolated IndexedDB database names
 * so the suite is safe to run on every dev launch without polluting
 * production state.
 *
 * Cases:
 *
 *   A. Row builder
 *    1. in-roster + trusted name → displayName uses trusted name
 *    2. in-roster + trustedDisplayName=null → stub
 *    3. not-in-roster → stub regardless of trustedDisplayName value
 *    4. displayHint = 'just now' for delta < 60 s
 *    5. displayHint buckets (minutes / hours / days / year-cap)
 *    6. blockedReason passes through unchanged
 *
 *   B. Filter
 *    7. empty query returns all rows
 *    8. 'ali' matches 'Alice' case-insensitively
 *    9. 'zzz' returns empty
 *   10. whitespace-only returns all; padded query trims
 *
 *   C. Sort
 *   11. 'recent' puts most recently blocked at index 0
 *   12. 'alphabetical' puts 'Alice' before 'Bob' (case-insensitive)
 *   13. 'oldest' is reverse of 'recent'
 *   14. stable tiebreaker: equal blockedAt preserves original order
 *
 *   D. Unblock handler
 *   15. unblock on blocked user → 'unblocked', payload.action = 'unblock'
 *   16. unblock on non-blocked user → 'skipNotBlocked' (idempotent)
 *
 *   E. Hardening
 *   17. displayName for non-roster never includes peer-supplied bytes
 *   18. filter is synchronous / in-memory only (no async path)
 *   19. filter + sort composition produces correct result
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { buildRow, unknownStub, formatHint } from "./BlockedUsersRowModel";
import type { BlockedUserRow } from "./BlockedUsersRowModel";
import { filter, sort } from "./BlockedUsersListPolicy";
import { unblock } from "./BlockedUsersUnblockHandler";
import { BlockListStore } from "../block-list/BlockListStore";
import type { BlockListEntry } from "../block-list/BlockListEntry";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Each call returns a BlockListStore backed by a fresh, isolated IDB database. */
function freshStore(): BlockListStore {
  dbCounter += 1;
  return new BlockListStore(
    `kaether:blocked-users:selftest:${dbCounter}:${Date.now()}`,
  );
}

function makeEntry(
  userId: string,
  blockedAt: number,
  reason: BlockListEntry["reason"] = "userInitiated",
): BlockListEntry {
  return { userId, blockedAt, reason };
}

function sampleRows(): BlockedUserRow[] {
  return [
    {
      userId: "u1",
      displayName: "Alice",
      humanisedHint: "just now",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
    {
      userId: "u2",
      displayName: "Bob",
      humanisedHint: "just now",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
    {
      userId: "u3",
      displayName: "carol",
      humanisedHint: "just now",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
    {
      userId: "u4",
      displayName: "u4_abc1… (Unknown user)",
      humanisedHint: "just now",
      isInRoster: false,
      blockedReason: "spamFlag",
    },
  ];
}

function sortEntries(): Map<string, BlockListEntry> {
  return new Map<string, BlockListEntry>([
    ["u1", makeEntry("u1", 100)],
    ["u2", makeEntry("u2", 300)],
    ["u3", makeEntry("u3", 200)],
  ]);
}

function sortRows(): BlockedUserRow[] {
  return [
    {
      userId: "u1",
      displayName: "Alice",
      humanisedHint: "",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
    {
      userId: "u2",
      displayName: "carol",
      humanisedHint: "",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
    {
      userId: "u3",
      displayName: "Bob",
      humanisedHint: "",
      isInRoster: true,
      blockedReason: "userInitiated",
    },
  ];
}

// ---------------------------------------------------------------------------
// A. Row builder
// ---------------------------------------------------------------------------

function testRowInRosterUsesTrustedName(): TestCase {
  const entry = makeEntry("user_alice_long_id", 1_700_000_000);
  const row = buildRow({
    entry,
    trustedDisplayName: "Alice",
    isInRoster: true,
    nowSeconds: 1_700_000_010,
  });
  const ok = row.displayName === "Alice" && row.userId === "user_alice_long_id";
  return ok
    ? pass("in-roster + trusted name → displayName uses trusted name")
    : fail(
        "in-roster + trusted name → displayName uses trusted name",
        `displayName=${row.displayName}`,
      );
}

function testRowInRosterButNullNameFallsBackToStub(): TestCase {
  const entry = makeEntry("user_alice_xyz", 1_700_000_000);
  const row = buildRow({
    entry,
    trustedDisplayName: null,
    isInRoster: true,
    nowSeconds: 1_700_000_010,
  });
  const expected = "user_ali… (Unknown user)";
  const ok = row.displayName === expected;
  return ok
    ? pass("in-roster but trustedDisplayName=null → stub")
    : fail(
        "in-roster but trustedDisplayName=null → stub",
        `displayName=${row.displayName} expected=${expected}`,
      );
}

function testRowNotInRosterAlwaysStubEvenWithName(): TestCase {
  // Even when the caller passes a non-null trustedDisplayName, the builder
  // MUST ignore it when isInRoster=false. This is the defensive belt-and-
  // braces against a buggy caller accidentally feeding peer-supplied bytes.
  const entry = makeEntry("user_bob_xyz", 1_700_000_000);
  const row = buildRow({
    entry,
    trustedDisplayName: "Bob (would-be peer-supplied)",
    isInRoster: false,
    nowSeconds: 1_700_000_010,
  });
  const expected = "user_bob… (Unknown user)";
  const ok = row.displayName === expected;
  return ok
    ? pass("not-in-roster → stub regardless of trustedDisplayName")
    : fail(
        "not-in-roster → stub regardless of trustedDisplayName",
        `displayName=${row.displayName} expected=${expected}`,
      );
}

function testRowHintJustNow(): TestCase {
  const entry = makeEntry("alice", 1_700_000_000);
  const row = buildRow({
    entry,
    trustedDisplayName: "Alice",
    isInRoster: true,
    nowSeconds: 1_700_000_030, // 30 s later
  });
  const ok = row.humanisedHint === "just now";
  return ok
    ? pass("displayHint = 'just now' for delta < 60 s")
    : fail(
        "displayHint = 'just now' for delta < 60 s",
        `hint=${row.humanisedHint}`,
      );
}

function testRowHintBuckets(): TestCase {
  const base = 1_700_000_000;
  const entry = makeEntry("alice", base);

  const m5 = formatHint(base, base + 5 * 60); // 5 minutes
  const h1 = formatHint(base, base + 1 * 60 * 60); // 1 hour
  const h3 = formatHint(base, base + 3 * 60 * 60); // 3 hours
  const d1 = formatHint(base, base + 1 * 86_400); // 1 day
  const d2 = formatHint(base, base + 2 * 86_400); // 2 days
  const yPlus = formatHint(base, base + 400 * 86_400); // >1 year

  const mOk = m5 === "5 minutes ago";
  const h1Ok = h1 === "1 hour ago";
  const h3Ok = h3 === "3 hours ago";
  const d1Ok = d1 === "1 day ago";
  const d2Ok = d2 === "2 days ago";
  const yOk = yPlus === "more than a year ago";
  const ok = mOk && h1Ok && h3Ok && d1Ok && d2Ok && yOk;

  return ok
    ? pass("displayHint buckets (minutes / hours / days / year-cap)")
    : fail(
        "displayHint buckets (minutes / hours / days / year-cap)",
        `5m=${m5} 1h=${h1} 3h=${h3} 1d=${d1} 2d=${d2} y=${yPlus}`,
      );
}

function testRowBlockedReasonPassesThrough(): TestCase {
  const reasons: BlockListEntry["reason"][] = [
    "userInitiated",
    "spamFlag",
    "calendarHarassment",
    "other",
  ];
  for (const r of reasons) {
    const entry: BlockListEntry = { userId: "u", blockedAt: 1, reason: r };
    const row = buildRow({
      entry,
      trustedDisplayName: "U",
      isInRoster: true,
      nowSeconds: 2,
    });
    if (row.blockedReason !== r) {
      return fail(
        "blockedReason passes through unchanged",
        `for ${r} got ${row.blockedReason}`,
      );
    }
  }
  return pass("blockedReason passes through unchanged");
}

// ---------------------------------------------------------------------------
// B. Filter
// ---------------------------------------------------------------------------

function testFilterEmptyReturnsAll(): TestCase {
  const rows = sampleRows();
  const result = filter(rows, "");
  const ok = result.length === rows.length;
  return ok
    ? pass("filter: empty query returns all rows")
    : fail(
        "filter: empty query returns all rows",
        `got ${result.length} expected ${rows.length}`,
      );
}

function testFilterCaseInsensitiveSubstring(): TestCase {
  const rows = sampleRows();
  const result = filter(rows, "ali");
  const ok = result.length === 1 && result[0]?.userId === "u1";
  return ok
    ? pass("filter: 'ali' matches 'Alice' case-insensitively")
    : fail(
        "filter: 'ali' matches 'Alice' case-insensitively",
        `got ${JSON.stringify(result.map((r) => r.displayName))}`,
      );
}

function testFilterNoMatchReturnsEmpty(): TestCase {
  const result = filter(sampleRows(), "zzz");
  const ok = result.length === 0;
  return ok
    ? pass("filter: 'zzz' returns empty")
    : fail(
        "filter: 'zzz' returns empty",
        `got ${JSON.stringify(result.map((r) => r.displayName))}`,
      );
}

function testFilterTrimsWhitespace(): TestCase {
  const rows = sampleRows();
  const blank = filter(rows, "   ");
  const padded = filter(rows, "  ali ");
  const ok = blank.length === rows.length && padded.length === 1 && padded[0]?.userId === "u1";
  return ok
    ? pass("filter: whitespace-only returns all; padded query trims")
    : fail(
        "filter: whitespace-only returns all; padded query trims",
        `blank=${blank.length} padded=${JSON.stringify(padded.map((r) => r.userId))}`,
      );
}

// ---------------------------------------------------------------------------
// C. Sort
// ---------------------------------------------------------------------------

function testSortRecentFirst(): TestCase {
  const rows = sortRows();
  const entries = sortEntries();
  const sorted = sort(rows, "recent", entries);
  // blockedAt: u1=100, u2=300, u3=200 → recent order: u2(300), u3(200), u1(100)
  const ok =
    sorted[0]?.userId === "u2" &&
    sorted[1]?.userId === "u3" &&
    sorted[2]?.userId === "u1";
  return ok
    ? pass("sort 'recent' puts most recently blocked at index 0")
    : fail(
        "sort 'recent' puts most recently blocked at index 0",
        `ids=${sorted.map((r) => r.userId).join(",")}`,
      );
}

function testSortAlphabetical(): TestCase {
  const rows = sortRows();
  const entries = sortEntries();
  const sorted = sort(rows, "alphabetical", entries);
  // Names: Alice(u1), carol(u2), Bob(u3) → case-insensitive: Alice, Bob, carol
  const ok =
    sorted[0]?.displayName === "Alice" &&
    sorted[1]?.displayName === "Bob" &&
    sorted[2]?.displayName === "carol";
  return ok
    ? pass("sort 'alphabetical' puts 'Alice' before 'Bob' (case-insensitive)")
    : fail(
        "sort 'alphabetical' puts 'Alice' before 'Bob' (case-insensitive)",
        `names=${sorted.map((r) => r.displayName).join(",")}`,
      );
}

function testSortOldestFirst(): TestCase {
  const rows = sortRows();
  const entries = sortEntries();
  const sorted = sort(rows, "oldest", entries);
  // blockedAt: u1=100, u3=200, u2=300 → oldest order: u1(100), u3(200), u2(300)
  const ok =
    sorted[0]?.userId === "u1" &&
    sorted[1]?.userId === "u3" &&
    sorted[2]?.userId === "u2";
  return ok
    ? pass("sort 'oldest' is reverse of 'recent'")
    : fail(
        "sort 'oldest' is reverse of 'recent'",
        `ids=${sorted.map((r) => r.userId).join(",")}`,
      );
}

function testSortStableTiebreaker(): TestCase {
  const entries = new Map<string, BlockListEntry>([
    ["u1", makeEntry("u1", 100)],
    ["u2", makeEntry("u2", 100)],
    ["u3", makeEntry("u3", 100)],
  ]);
  const rows: BlockedUserRow[] = [
    { userId: "u1", displayName: "x", humanisedHint: "", isInRoster: true, blockedReason: "userInitiated" },
    { userId: "u2", displayName: "y", humanisedHint: "", isInRoster: true, blockedReason: "userInitiated" },
    { userId: "u3", displayName: "z", humanisedHint: "", isInRoster: true, blockedReason: "userInitiated" },
  ];
  const mr = sort(rows, "recent", entries);
  const ol = sort(rows, "oldest", entries);
  const ok =
    mr.map((r) => r.userId).join(",") === "u1,u2,u3" &&
    ol.map((r) => r.userId).join(",") === "u1,u2,u3";
  return ok
    ? pass("sort is stable when blockedAt equals (preserves original order)")
    : fail(
        "sort is stable when blockedAt equals (preserves original order)",
        `recent=${mr.map((r) => r.userId).join(",")} oldest=${ol.map((r) => r.userId).join(",")}`,
      );
}

// ---------------------------------------------------------------------------
// D. Unblock handler
// ---------------------------------------------------------------------------

async function testUnblockBlockedUserProceeds(): Promise<TestCase> {
  const store = freshStore();
  await store.block("alice", 100, "userInitiated");
  const outcome = await unblock("alice", store, 200);
  if (outcome.kind !== "unblocked") {
    return fail(
      "unblock on blocked user → 'unblocked' with payload",
      `got kind=${outcome.kind}`,
    );
  }
  const stillBlocked = await store.isBlocked("alice");
  const ok =
    outcome.payload.blockedUserId === "alice" &&
    outcome.payload.action === "unblock" &&
    outcome.payload.ts === 200 &&
    !stillBlocked;
  return ok
    ? pass("unblock on blocked user → 'unblocked' with payload")
    : fail(
        "unblock on blocked user → 'unblocked' with payload",
        `payload=${JSON.stringify(outcome.payload)} stillBlocked=${stillBlocked}`,
      );
}

async function testUnblockNonBlockedUserSkips(): Promise<TestCase> {
  const store = freshStore();
  const outcome = await unblock("ghost", store);
  const ok = outcome.kind === "skipNotBlocked";
  return ok
    ? pass("unblock on non-blocked user → 'skipNotBlocked' (idempotent)")
    : fail(
        "unblock on non-blocked user → 'skipNotBlocked' (idempotent)",
        `got kind=${outcome.kind}`,
      );
}

// ---------------------------------------------------------------------------
// E. Hardening
// ---------------------------------------------------------------------------

function testStubNeverIncludesPeerSuppliedBytes(): TestCase {
  // For a not-in-roster user the displayName must be derived ENTIRELY from
  // the userId prefix and a fixed suffix. No matter what we put into
  // trustedDisplayName, it must not appear in the rendered string.
  const payloads = [
    "<script>alert('x')</script>",
    "Alice (​‎invisible)",
    "💀 spoof name",
    "Z".repeat(1024),
  ];
  for (const p of payloads) {
    const entry = makeEntry("user_target_id", 1);
    const row = buildRow({
      entry,
      trustedDisplayName: p,
      isInRoster: false,
      nowSeconds: 2,
    });
    if (row.displayName.includes(p)) {
      return fail(
        "displayName for non-roster never includes peer-supplied bytes",
        `displayName leaked payload: ${row.displayName.slice(0, 40)}`,
      );
    }
    const expected = "user_tar… (Unknown user)";
    if (row.displayName !== expected) {
      return fail(
        "displayName for non-roster never includes peer-supplied bytes",
        `displayName drifted: ${row.displayName} expected: ${expected}`,
      );
    }
  }
  return pass("displayName for non-roster never includes peer-supplied bytes");
}

function testFilterIsInMemoryOnly(): TestCase {
  // The filter API takes (BlockedUserRow[], string) → BlockedUserRow[].
  // It cannot take a network handle, can't return a Promise, and can't
  // suspend. We verify by calling it and asserting the return is
  // synchronous (non-Promise). The compile-time guarantee comes from the
  // function signature in BlockedUsersListPolicy; this test documents it.
  const rows = sampleRows();
  const result = filter(rows, "ali");
  const isSynchronous = !(result instanceof Promise);
  const ok = isSynchronous && result.length === 1 && result[0]?.userId === "u1";
  return ok
    ? pass("filter operates only on in-memory rows (no async/network egress)")
    : fail(
        "filter operates only on in-memory rows (no async/network egress)",
        `isSynchronous=${isSynchronous} resultLen=${(result as BlockedUserRow[]).length}`,
      );
}

function testFilterThenSortComposition(): TestCase {
  // Filter for names containing "o" (Bob, carol), then sort alphabetically.
  // Expected: Bob before carol (case-insensitive).
  const rows = sampleRows();
  const entries = new Map<string, BlockListEntry>([
    ["u1", makeEntry("u1", 100)],
    ["u2", makeEntry("u2", 300)],
    ["u3", makeEntry("u3", 200)],
    ["u4", makeEntry("u4", 50)],
  ]);
  const filtered = filter(rows, "o"); // matches Bob(u2), carol(u3)
  const sorted = sort(filtered, "alphabetical", entries);
  const ok =
    sorted.length === 2 &&
    sorted[0]?.displayName === "Bob" &&
    sorted[1]?.displayName === "carol";
  return ok
    ? pass("filter + sort composition produces correct ordering")
    : fail(
        "filter + sort composition produces correct ordering",
        `names=${sorted.map((r) => r.displayName).join(",")}`,
      );
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Blocked-users UI (view model + filter)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const syncCases: TestCase[] = [
      // A. Row builder
      testRowInRosterUsesTrustedName(),
      testRowInRosterButNullNameFallsBackToStub(),
      testRowNotInRosterAlwaysStubEvenWithName(),
      testRowHintJustNow(),
      testRowHintBuckets(),
      testRowBlockedReasonPassesThrough(),
      // B. Filter
      testFilterEmptyReturnsAll(),
      testFilterCaseInsensitiveSubstring(),
      testFilterNoMatchReturnsEmpty(),
      testFilterTrimsWhitespace(),
      // C. Sort
      testSortRecentFirst(),
      testSortAlphabetical(),
      testSortOldestFirst(),
      testSortStableTiebreaker(),
      // E. Hardening (sync)
      testStubNeverIncludesPeerSuppliedBytes(),
      testFilterIsInMemoryOnly(),
      testFilterThenSortComposition(),
    ];

    // D. Unblock handler (async — IDB)
    const asyncCases: TestCase[] = await Promise.all([
      testUnblockBlockedUserProceeds(),
      testUnblockNonBlockedUserSkips(),
    ]);

    return buildSuiteResult([...syncCases, ...asyncCases]);
  },
});
